import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate, CandidateRole, CandidateDepartment, CandidateSource } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { UserPlus, Phone, Users } from 'lucide-react';
interface AddCandidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (candidate: Candidate) => void;
  defaultStatus?: Candidate['status'];
}
const ROLES: CandidateRole[] = ['Team Lead', 'Loan Officer', 'Loan Partner', 'Telemarketer', 'Processor', 'Processing Manager', 'Admin', 'Human Resources'];
const DEPARTMENTS: CandidateDepartment[] = ['Sales', 'Operations', 'Processing'];
const SOURCES: CandidateSource[] = ['LinkedIn', 'Indeed', 'Referral', 'Social Media', 'ZipRecruiter'];
const RECRUITERS = ['Mirna Hermiz'];
export function AddCandidateModal({
  isOpen,
  onClose,
  onCreate,
  defaultStatus = 'Phone Screen'
}: AddCandidateModalProps) {
  const {
    addToast
  } = useToast();
  // Starting stage: Phone Screen or In Person Interview.
  const initialStartStage: 'Phone Screen' | 'In Person Interview' = defaultStatus === 'In Person Interview' ? 'In Person Interview' : 'Phone Screen';
  const [status, setStatus] = useState<'Phone Screen' | 'In Person Interview'>(initialStartStage);
  // Re-sync the starting stage whenever the modal is opened with a new
  // defaultStatus (e.g. the Recruiting page passes "In Person Interview").
  // Without this, useState's initial value is only used on first mount and
  // subsequent opens keep the previous stage selection.
  useEffect(() => {
    if (isOpen) {
      setStatus(initialStartStage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultStatus]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<CandidateRole>('Loan Officer');
  const [department, setDepartment] = useState<CandidateDepartment>('Sales');
  const [recruiter, setRecruiter] = useState(RECRUITERS[0]);
  const [source, setSource] = useState<CandidateSource>('LinkedIn');
  const [stageDate, setStageDate] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpReason, setFollowUpReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setStatus(initialStartStage);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRole('Loan Officer');
    setDepartment('Sales');
    setRecruiter(RECRUITERS[0]);
    setSource('LinkedIn');
    setStageDate('');
    setNotes('');
    setErrors({});
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };
  const dateLabel = status === 'Phone Screen' ? 'Phone screen date' : 'Interview date';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = 'Required';
    if (!lastName.trim()) errs.lastName = 'Required';
    if (!email.trim()) errs.email = 'Required';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Invalid email';
    if (!phone.trim()) errs.phone = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const today = new Date().toISOString().split('T')[0];
    const newCandidate: Candidate = {
      id: `c_${Date.now()}`,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      role,
      department,
      branch: 'East',
      status,
      interviewedBy: recruiter,
      recruiter,
      candidateSource: source,
      phoneInterviewDate: stageDate || today,
      ...(status === 'In Person Interview' && stageDate ? {
        inPersonInterviewDate: new Date(stageDate).toISOString()
      } : {}),
      notes: notes.trim() || undefined,
      stageHistory: [{
        status,
        date: today
      }]
    };
    onCreate(newCandidate);
    addToast({
      type: 'success',
      title: 'Candidate added',
      message: `${newCandidate.firstName} ${newCandidate.lastName} added to ${status}.`
    });
    resetForm();
    onClose();
  };
  const inputBase = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white';
  const errBorder = (k: string) => errors[k] ? 'border-red-300' : 'border-gray-300';
  return <Modal isOpen={isOpen} onClose={handleClose} title="Add Candidate" maxWidth="2xl" data-id="element-6">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-7">
        {/* Starting stage — Phone Screen or Interview */}
        <div data-id="element-8">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-9">
            Starting stage
          </p>
          <div className="grid grid-cols-2 gap-2" data-id="element-10">
            {([{
            value: 'Phone Screen',
            label: 'Phone Screen',
            hint: 'Start with a screening call',
            Icon: Phone
          }, {
            value: 'In Person Interview',
            label: 'Interview',
            hint: 'Skip the phone screen',
            Icon: Users
          }] as const).map(({
            value,
            label,
            hint,
            Icon
          }) => {
            const active = status === value;
            return <button key={value} type="button" onClick={() => setStatus(value)} className={`text-left p-3 rounded-lg border transition-all flex items-center gap-3 ${active ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-100' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-11">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center border ${active ? 'bg-white border-orange-200 text-orange-600' : 'bg-gray-50 border-gray-200 text-gray-500'}`} data-id="element-12">
                    <Icon className="h-4 w-4" data-id="element-13" />
                  </div>
                  <div data-id="element-14">
                    <p className={`text-sm font-semibold ${active ? 'text-orange-700' : 'text-gray-900'}`} data-id="element-15">
                      {label}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-tight" data-id="element-16">
                      {hint}
                    </p>
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Identity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17">
          <div data-id="element-18">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-19">
              First name
            </label>
            <input value={firstName} onChange={e => setFirstName(e.target.value)} className={`${inputBase} ${errBorder('firstName')}`} data-id="element-20" />
            {errors.firstName && <p className="text-xs text-red-600 mt-1" data-id="element-21">{errors.firstName}</p>}
          </div>
          <div data-id="element-22">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-23">
              Last name
            </label>
            <input value={lastName} onChange={e => setLastName(e.target.value)} className={`${inputBase} ${errBorder('lastName')}`} data-id="element-24" />
            {errors.lastName && <p className="text-xs text-red-600 mt-1" data-id="element-25">{errors.lastName}</p>}
          </div>
          <div data-id="element-26">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-27">
              Email
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={`${inputBase} ${errBorder('email')}`} data-id="element-28" />
            {errors.email && <p className="text-xs text-red-600 mt-1" data-id="element-29">{errors.email}</p>}
          </div>
          <div data-id="element-30">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-31">
              Phone
            </label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" className={`${inputBase} ${errBorder('phone')}`} data-id="element-32" />
            {errors.phone && <p className="text-xs text-red-600 mt-1" data-id="element-33">{errors.phone}</p>}
          </div>
        </div>

        {/* Role / department */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-34">
          <div data-id="element-35">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-36">
              Role
            </label>
            <select value={role} onChange={e => setRole(e.target.value as CandidateRole)} className={`${inputBase} border-gray-300`} data-id="element-37">
              {ROLES.map(r => <option key={r} value={r} data-id="element-38">
                  {r}
                </option>)}
            </select>
          </div>
          <div data-id="element-39">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-40">
              Department
            </label>
            <select value={department} onChange={e => setDepartment(e.target.value as CandidateDepartment)} className={`${inputBase} border-gray-300`} data-id="element-41">
              {DEPARTMENTS.map(d => <option key={d} value={d} data-id="element-42">
                  {d}
                </option>)}
            </select>
          </div>
        </div>

        {/* Recruiter / source */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-43">
          <div data-id="element-44">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-45">
              Recruiter
            </label>
            <select value={recruiter} onChange={e => setRecruiter(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-46">
              {RECRUITERS.map(r => <option key={r} value={r} data-id="element-47">
                  {r}
                </option>)}
            </select>
          </div>
          <div data-id="element-48">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-49">
              Source
            </label>
            <select value={source} onChange={e => setSource(e.target.value as CandidateSource)} className={`${inputBase} border-gray-300`} data-id="element-50">
              {SOURCES.map(s => <option key={s} value={s} data-id="element-51">
                  {s}
                </option>)}
            </select>
          </div>
        </div>

        {/* Conditional stage date */}
        {dateLabel && <div data-id="element-52">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-53">
              {dateLabel}{' '}
              <span className="text-gray-400 font-normal" data-id="element-54">(optional)</span>
            </label>
            <input type="datetime-local" value={stageDate} onChange={e => setStageDate(e.target.value)} className={`${inputBase} border-gray-300`} data-id="element-55" />
          </div>}

        {/* Conditional follow-up fields */}
        {status === 'Follow Up Later' && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-orange-50/40 border border-orange-100" data-id="element-56">
            <div data-id="element-57">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-58">
                Follow-up date
              </label>
              <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className={`${inputBase} ${errBorder('followUpDate')}`} data-id="element-59" />
              {errors.followUpDate && <p className="text-xs text-red-600 mt-1" data-id="element-60">
                  {errors.followUpDate}
                </p>}
            </div>
            <div data-id="element-61">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-62">
                Reason
              </label>
              <input value={followUpReason} onChange={e => setFollowUpReason(e.target.value)} placeholder="e.g. Revisit in Q3" className={`${inputBase} border-gray-300`} data-id="element-63" />
            </div>
          </div>}

        <div data-id="element-64">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-65">
            Notes <span className="text-gray-400 font-normal" data-id="element-66">(optional)</span>
          </label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={`${inputBase} border-gray-300 resize-none`} data-id="element-67" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100" data-id="element-68">
          <Button type="button" variant="secondary" onClick={handleClose} data-id="element-69">
            Cancel
          </Button>
          <Button type="submit" variant="primary" leftIcon={<UserPlus className="h-4 w-4" data-id="element-71" />} data-id="element-70">
            Add Candidate
          </Button>
        </div>
      </form>
    </Modal>;
}